import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, Chip, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import AddIcon from "@mui/icons-material/Add";
import { useConstants } from "../../hooks/UseConstants";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import AdsDetails from "../../popup/AdsDetails";
import AddAds from "../../popup/AddAds";
import UpdateAds from "../../popup/UpdateAds";
import DeleteDialog from "../../popup/DeleteDialog";
import AdsFilter from "../../popup/AdsFilter";
import { useAdsFilter } from "../../filter/UseAdsFilter";
import { FormattedMessage, useIntl } from "react-intl";
import { usePagination } from "../../hooks/UsePagination";
import { useSearch } from "../../hooks/UseSearch";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import AdminListToolbar from "../../components/AdminListToolbar";
import ModalOverlay from "../../components/ModalOverlay";

function formatDate(value) {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toISOString().split("T")[0];
}

function Ads() {
    const theme = useTheme();
    const intl = useIntl();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch, order, setOrder } = useSearch();
    const { getWait, setGetWait, filterWait, setFilterWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const { activeFrom, setActiveFrom, activeUntil, setActiveUntil, category, setCategory, status, setStatus } = useAdsFilter();
    const [bannersCounts, setBannersCounts] = useState(0);
    const [banners, setBanners] = useState([]);
    const [banner, setBanner] = useState(null);
    const [bannerId, setBannerId] = useState("");

    const bannersUrl = useMemo(() => (
        `${host}/banners?status=${status}&page=${page + 1}&search=${search}&direction=asc&${order ? `order_by=${order}` : ""}${category ? `&category=${category}` : ""}${activeFrom ? `&active_from=${activeFrom}` : ""}${activeUntil ? `&active_until=${activeUntil}` : ""}`
    ), [host, status, page, search, order, category, activeFrom, activeUntil]);

    const getBannerStatus = (item) => {
        const now = new Date();
        const start = item?.active_from ? new Date(item.active_from) : null;
        const end = item?.active_until ? new Date(item.active_until) : null;

        if (start && now < start) {
            return {
                key: "not_started",
                label: isAr ? intl.formatMessage({ id: "not_started" }) : "Not started",
                color: "warning",
            };
        }
        if (!item?.is_active) {
            return {
                key: "stopped",
                label: isAr ? intl.formatMessage({ id: "stopped" }) : "Stopped",
                color: "default",
            };
        }
        if (end && now > end) {
            return {
                key: "ended",
                label: isAr ? intl.formatMessage({ id: "finished" }) : "Finished",
                color: "error",
            };
        }
        return {
            key: "active",
            label: isAr ? intl.formatMessage({ id: "active" }) : "Active",
            color: "success",
        };
    };

    const getBanners = async () => {
        const result = await Fetch(bannersUrl, "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setBannersCounts(pagination.total);
            setBanners(items);
            setCurrentPage(page);
        } else {
            setBanners([]);
            setBannersCounts(0);
        }

        setGetWait(false);
    };

    const filteringBanners = async () => {
        const result = await Fetch(bannersUrl, "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setBannersCounts(pagination.total);
            setBanners(items);
            setCurrentPage(page);
            setPopup("filter", "none");
        }

        setFilterWait(false);
    };

    const updateBanner = (id) => {
        setBanner(banners.find((item) => item.id === id) || null);
        setPopup("update", "flex");
    };

    const bannerDetails = (id) => {
        setBanner(banners.find((item) => item.id === id) || null);
        setPopup("details", "flex");
    };

    const deleteBanner = async () => {
        const result = await Fetch(`${host}/admin/banners/${bannerId}/delete`, "DELETE", null);

        if (result.status === 200) {
            setBanners((prev) => prev.filter((item) => item.id !== bannerId));
            setBannersCounts((prev) => Math.max(Number(prev || 0) - 1, 0));
            setSnackBar("success", isAr ? "تم حذف الإعلان بنجاح" : "Ad deleted successfully");
            setPopup("delete", "none");
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getBanners();
        }
    }, [wait, page, search, order]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <AdminPageHero
                    actions={
                        <Button variant="contained" onClick={() => setPopup("add", "flex")} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.35)" } }}>
                            <AddIcon fontSize="small" />
                            <FormattedMessage id="add_ads" />
                        </Button>
                    }
                />

                <DashboardPanelCard
                    title={isAr ? "إدارة الإعلانات" : "Ads management"}
                    subtitle={isAr ? "تابع حالة الإعلانات ومواعيدها وصلتها بالدورات أو المسارات من جدول أوضح." : "Track ad status, schedule, and linked course or path from a clearer management table."}
                    className="mx-0 !mt-0"
                >
                    <AdminListToolbar
                        isAr={isAr}
                        searchPlaceholder={intl.formatMessage({ id: "search_ads" })}
                        search={search}
                        onSearchChange={setSearch}
                        order={order}
                        onOrderChange={setOrder}
                        totalLabel={intl.formatMessage({ id: "total_ads" })}
                        totalCount={bannersCounts}
                        onFilterClick={() => setPopup("filter", "flex")}
                        orderOptions={[
                            { value: "", label: intl.formatMessage({ id: "date" }) },
                            { value: language === "en" ? "name_en" : "name_ar", label: intl.formatMessage({ id: "ads_title" }) },
                        ]}
                    />

                    <TableContainer component={Paper} dir={language === "en" ? "ltr" : "rtl"} sx={{ boxShadow: "none", borderRadius: 3 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="ads table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="ads_title" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="ads_type" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="category" />/<FormattedMessage id="course" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="start_date" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="end_date" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center"><FormattedMessage id="ads_state" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center"><FormattedMessage id="procedures" /></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {banners.length > 0 ? banners.map((item) => {
                                    const statusMeta = getBannerStatus(item);
                                    return (
                                        <StyledTableRow
                                            key={item.id}
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: theme.palette.mode === "dark" ? "action.hover" : "#f8fafc",
                                                },
                                            }}
                                            onClick={() => bannerDetails(item.id)}
                                        >
                                            <StyledTableCell align={language === "en" ? "left" : "right"} component="th" scope="row">
                                                {language === "en" ? item.name_en : item.name_ar}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {item.category === "course" ? <FormattedMessage id="course" /> : <FormattedMessage id="path" />}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {language === "en" ? item.related_data?.name_en : item.related_data?.name_ar}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>{formatDate(item.active_from)}</StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>{formatDate(item.active_until)}</StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"} className="!text-center">
                                                <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant="outlined" />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: language === "en" ? "flex-start" : "flex-end" }}>
                                                    <Button
                                                        variant="contained"
                                                        className="!bg-red-300 !font-bold !text-red-700 hover:!bg-red-500 hover:!text-white duration-300"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setBannerId(item.id);
                                                            setPopup("delete", "flex");
                                                        }}
                                                    >
                                                        <FormattedMessage id="delete" />
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        className="!bg-green-300 !font-bold !text-green-800 hover:!bg-green-500 hover:!text-white duration-300"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            updateBanner(item.id);
                                                        }}
                                                    >
                                                        <FormattedMessage id="update" />
                                                    </Button>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                }) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={7} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                {isAr ? "لا توجد إعلانات مطابقة للبحث أو التصفية الحالية." : "No ads match the current search or filters."}
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }} dir="rtl">
                        <Button disabled={page + 1 === totalPages || totalPages === 0} onClick={() => setPage(currentPage + 1)}>
                            <NavigateNextIcon fontSize="large" />
                        </Button>
                        <Typography variant="body1" className="!text-xl" dir="ltr">
                            {totalPages ? currentPage + 1 : 0} / {totalPages || 0}
                        </Typography>
                        <Button disabled={page + 1 === 1} onClick={() => setPage(currentPage - 1)}>
                            <NavigateBeforeIcon fontSize="large" />
                        </Button>
                    </Box>
                </DashboardPanelCard>
            </PageContent>

            <ModalOverlay id="filter" onClose={() => setPopup("filter", "none")}>
                <AdsFilter onClickClose={() => setPopup("filter", "none")} onClickConfirm={filteringBanners} status={status} setStatus={setStatus} category={category} setCategory={setCategory} activeFrom={activeFrom} setActiveFrom={setActiveFrom} activeUntil={activeUntil} setActiveUntil={setActiveUntil} filterWait={filterWait} setFilterWait={setFilterWait} />
            </ModalOverlay>

            <ModalOverlay id="add" onClose={() => setPopup("add", "none")}>
                <AddAds onClickClose={() => setPopup("add", "none")} setSnackbar={setSnackBar} getBanners={getBanners} />
            </ModalOverlay>

            <ModalOverlay id="update" onClose={() => setPopup("update", "none")}>
                <UpdateAds setSnackbar={setSnackBar} setBanner={setBanner} banner={banner} onClickClose={() => setPopup("update", "none")} getBanners={getBanners} />
            </ModalOverlay>

            <ModalOverlay id="delete" onClose={() => setPopup("delete", "none")}>
                <DeleteDialog onClickConfirm={deleteBanner} onClickCancel={() => setPopup("delete", "none")} title={<FormattedMessage id="ads_delete" />} subtitle={<FormattedMessage id="ads_delete_description" />} />
            </ModalOverlay>

            <ModalOverlay id="details" onClose={() => setPopup("details", "none")}>
                {banner ? <AdsDetails onClickClose={() => setPopup("details", "none")} setSnackBar={setSnackBar} setBanners={setBanners} banner={banner} onClickUpdate={updateBanner} /> : null}
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Ads;